import { Check, X, TrendingUp, Shield, Users, Settings, BookOpen, Target, Sparkles } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

const QualificationSection = () => {
  const forYouItems = [
    "You're frustrated with generic agency output",
    "You want to build authentic, founder-led content",
    "You're ready to invest ₹5+ Lakhs for long-term capability",
    "You value ownership over temporary solutions",
    "You can dedicate 60-90 minutes per week during the build phase"
  ];

  const notForYouItems = [
    "You're looking for a quick, cheap fix",
    "You want someone else to handle everything permanently",
    "You're not ready to invest in building internal capabilities",
    "You prefer the agency dependency model",
    "You can't commit time to the training process"
  ];

  const investmentBenefits = [
    {
      icon: Users,
      title: "Your own dedicated content team",
      description: "(permanent employees)"
    },
    {
      icon: Target,
      title: "Elite team trained exclusively",
      description: "for YOUR brand"
    },
    {
      icon: Settings,
      title: "Complete content operating system",
      description: "and workflows"
    },
    {
      icon: TrendingUp,
      title: "90 days of intensive training",
      description: "and optimization"
    },
    {
      icon: Shield,
      title: "Ongoing support during",
      description: "transition period"
    },
    {
      icon: BookOpen,
      title: "Full knowledge transfer",
      description: "and documentation"
    }
  ];

  return (
    <section className="section-spacing bg-gradient-to-b from-background to-accent/5 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-1/4 left-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Header */}
        <ScrollReveal delay={100}>
          <div className="text-center mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 glass-panel rounded-full border border-accent/20">
              <Target className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium text-accent">Partnership Criteria</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-display font-bold mb-6 lg:mb-8 leading-tight text-foreground">
              We Partner With Founders Who{" "}
              <span className="bg-gradient-brand bg-clip-text text-transparent">
                Build for the Long Term
              </span>
            </h2>
          </div>
        </ScrollReveal>

        {/* Qualification Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto mb-16 lg:mb-20">
          {/* This is for you */}
          <ScrollReveal delay={200}>
            <div className="glass-panel p-6 lg:p-8 rounded-2xl border border-primary/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-full">
                  <Check className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl lg:text-2xl font-display font-bold text-primary">
                  This is for you if...
                </h3>
              </div>
              
              <div className="space-y-4">
                {forYouItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors">
                    <div className="p-1 bg-primary/20 rounded-full mt-1">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm lg:text-base text-foreground/90 leading-relaxed">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>

          {/* This is NOT for you */}
          <ScrollReveal delay={300}>
            <div className="glass-panel p-6 lg:p-8 rounded-2xl border border-destructive/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-destructive/10 rounded-full">
                  <X className="w-5 h-5 text-destructive" />
                </div>
                <h3 className="text-xl lg:text-2xl font-display font-bold text-destructive">
                  This is NOT for you if...
                </h3>
              </div>
              
              <div className="space-y-4">
                {notForYouItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-destructive/5 hover:bg-destructive/10 transition-colors">
                    <div className="p-1 bg-destructive/20 rounded-full mt-1">
                      <X className="w-3 h-3 text-destructive" />
                    </div>
                    <span className="text-sm lg:text-base text-foreground/90 leading-relaxed">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>

        {/* Investment Comparison */}
        <ScrollReveal delay={400}>
          <div className="text-center mb-12 lg:mb-16">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mb-4 text-foreground">
              An Investment in Capability,{" "}
              <span className="text-destructive">Not a Recurring Expense</span>
            </h3>
          </div>
        </ScrollReveal>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto mb-16 lg:mb-20">
          {/* Agency Model */}
          <ScrollReveal delay={500}>
            <div className="glass-panel p-6 lg:p-8 rounded-2xl border border-destructive/20 relative">
              <div className="absolute top-4 right-4 w-3 h-3 bg-destructive rounded-full animate-pulse" />
              
              <div className="text-center">
                <h4 className="text-xl font-display font-bold text-destructive mb-4">
                  Agency Model
                </h4>
                
                <div className="mb-6">
                  <div className="text-sm text-muted-foreground mb-2">Annual Cost</div>
                  <div className="text-3xl lg:text-4xl font-display font-bold text-destructive mb-3">
                    ₹30+ Lakhs/year
                  </div>
                  <div className="text-sm text-foreground/70 bg-destructive/5 p-3 rounded-lg">
                    Endless retainers with no permanent value
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Contencraft Model */}
          <ScrollReveal delay={600}>
            <div className="glass-panel p-6 lg:p-8 rounded-2xl border border-primary/20 relative bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="absolute top-4 right-4 w-3 h-3 bg-primary rounded-full animate-pulse" />
              
              <div className="text-center">
                <h4 className="text-xl font-display font-bold text-primary mb-4">
                  Contencraft Model
                </h4>
                
                <div className="mb-6">
                  <div className="text-sm text-muted-foreground mb-2">One-Time Investment</div>
                  <div className="text-3xl lg:text-4xl font-display font-bold bg-gradient-brand bg-clip-text text-transparent mb-3">
                    ₹5+ Lakhs
                  </div>
                  <div className="text-sm text-foreground/70 bg-primary/5 p-3 rounded-lg">
                    Permanent team + systems that appreciate in value
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>

        {/* What You Get */}
        <ScrollReveal delay={700}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mb-4 text-foreground">
                What You Get for Your Investment
              </h3>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {investmentBenefits.map((benefit, index) => (
                <div key={index} className="glass-panel p-6 rounded-xl border border-accent/20 text-center hover:-translate-y-2 transition-all duration-300">
                  <div className="w-12 h-12 mx-auto mb-4 bg-accent/10 rounded-full flex items-center justify-center">
                    <benefit.icon className="w-6 h-6 text-accent" />
                  </div>
                  <h4 className="text-base font-semibold text-foreground mb-2 leading-tight">
                    {benefit.title}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Final Value Prop */}
            <div className="glass-panel p-6 lg:p-8 rounded-2xl border border-accent/20 bg-gradient-to-r from-accent/5 to-primary/5 text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-accent" />
                <span className="text-sm font-medium text-accent uppercase tracking-wide">The Math</span>
              </div>
              
              <p className="text-lg lg:text-xl text-foreground leading-relaxed">
                <span className="font-bold text-accent">Save ₹25+ Lakhs annually</span> while building a permanent asset that{" "}
                <span className="bg-gradient-brand bg-clip-text text-transparent font-semibold">
                  grows in value and capability over time
                </span>
              </p>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default QualificationSection;